import React, { useState, useEffect } from "react";
import { useVerbatim } from "../context/VerbatimContext";
import { useSetup } from "../context/SetupContext"; // Import the Setup context
import { extractDomainName } from "../utils/URLUtils";
import { useNavigate } from "react-router-dom"; // Import the navigation hook
import VerbatimFilter from "../components/VerbatimFilter"; // Import the filter function
import PrimaryButton from "../components/buttons/PrimaryButton";
import DeleteButton from "../components/buttons/DeleteButton";
import FilterBar from "../components/FilterBar";
import BatchBar from "../components/BatchBar";
import LabelChip from "../components/LabelChip";
import FocusEditingModal from "../modals/FocusEditingModal";
import MergeLabelsModal from "../modals/MergeLabelsModal";
import MergeLabelTypesModal from "../modals/MergeLabelTypesModal";
import ImageModal from '../modals/ImageModal';
import { base64ToImage, renderVerbatimTranslation, getVerbatimDefectInfo } from "../utils/VerbatimUtils"; // Import the utility function for base64 to image conversion
import "./BatchEditPage.css";

const BatchEditPage = () => {
  const {
    verbatims,
    setVerbatims,
    setVerbatimToEdit,
    verbatimsToEdit,
    editVerbatim,
    setVerbatimsToEdit, // Now using verbatimsToEdit for batch editing
    deleteVerbatims,
  } = useVerbatim();

  const { labels, labelTypes, setLabels } = useSetup(); // Use the labels from the Setup context
  const [filteredVerbatims, setFilteredVerbatims] = useState(verbatims); // Filtered verbatims to display
  const [currentPage, setCurrentPage] = useState(1); // Current page state
  const [selectedLabel, setSelectedLabel] = useState(""); // Selected label for batch application
  const [selectedLabelType, setSelectedLabelType] = useState(""); // Track selected label type
  const [selectedAction, setSelectedAction] = useState(""); // Track action for multi-value label types
  const [isFocusEditingOpen, setIsFocusEditingOpen] = useState(false);
  const [isCollapsibleOpen, setIsCollapsibleOpen] = useState(false);
  const [verbatimsPerPage, setVerbatimsPerPage] = useState(20);
  const [searchQuery, setSearchQuery] = useState(""); // New state for search query
  const navigate = useNavigate(); // Initialize navigation hook
  const [selectedSentimentState, setSelectedSentimentState] = useState("");
  const [modalImage, setModalImage] = useState(null); // State for the image modal
  const [resetFilters, setResetFilters] = useState(false); // State to reset filters
  const [resetBatchBarFields, setResetBatchBarFields] = useState(false); // State to reset batch bar fields
  const [isMergeLabelsModalOpen, setIsMergeLabelsModalOpen] = useState(false); // State for Merge Attributes Modal
  const [isMergeLabelTypesModalOpen, setIsMergeLabelTypesModalOpen] = useState(false); // State for Merge Label Types Modal
  const [showOnlyDefective, setShowOnlyDefective] = useState(false); // Show only verbatims to correct

  const [filters, setFilters] = useState({
    source: "",
    dynamicFilters: Object.keys(labelTypes).reduce((acc, labelType) => {
      acc[labelType] = [];
      return acc;
    }, {}),
  }); // Initialize filters state

  // Function to handle "Batch Free Editing"
  const handleBatchFreeEditing = () => {
    if (verbatimsToEdit.length > 0) {
      handleBatchEditVerbatim(0); // Start batch edit with the first verbatim (ID)
    }
  };

  const handleFocusEditing = (labelType, label, sentimentState, action) => {
    setSelectedLabelType(labelType);
    setSelectedLabel(label);
    setSelectedSentimentState(sentimentState); // Pass the sentiment state to the modal
    setSelectedAction(action); // Pass the action to the modal
    setIsFocusEditingOpen(true); // Open the Focus Editing modal
  };

  const handleApplyLabelInModal = (
    verbatimId,
    labelType,
    label,
    sentimentState,
    action
  ) => {
    console.log(
      `Applying label ${label} (${labelType}) to verbatim ID ${verbatimId}`
    );
  
    // Get the target verbatim
    const targetVerbatim = verbatims.find((v) => v.id === verbatimId);
  
    if (!targetVerbatim) {
      console.warn(`Verbatim with ID ${verbatimId} not found.`);
      return;
    }
  
    const newLabel = {
      name: label,
      state: sentimentState || "active", // Use selected sentiment state if provided
      type: labelType,
    };
  
    const labelGroup = targetVerbatim.labels[labelType] || [];
    const labelIndex = labelGroup.findIndex((l) => l.name === label);
  
    // Prepare updated labels
    let updatedLabels = { ...targetVerbatim.labels };
  
    if (labelTypes[labelType]?.multiValue === false) {
      // Replace with new label if multiValue is false
      updatedLabels[labelType] = [newLabel];
    } else {
      if (action === "add" &&labelIndex !== -1) {
        // If label exists, update its sentiment state
        updatedLabels[labelType] = labelGroup.map((l, index) =>
          index === labelIndex ? newLabel : l
        );
      } else if (action === "add" && labelIndex === -1) {
        // Add new label if it doesn't exist
        updatedLabels[labelType] = [...labelGroup, newLabel];
      } else if (action === "remove") {
        // Remove the label if it exists
        updatedLabels[labelType] = labelGroup.filter((l) => l.name !== label);
      }
    }
    // Use editVerbatim to update the specific verbatim
    editVerbatim(verbatimId, { labels: updatedLabels });
  };
    

  const fullVerbatimsToEdit = verbatims
    .filter((v) => verbatimsToEdit.includes(v.id))
    .reverse();

  // Function to edit multiple verbatims one-by-one
  const handleBatchEditVerbatim = (index) => {
    if (index < verbatimsToEdit.length) {
      const selectedVerbatimId = verbatimsToEdit[index]; // Get the current verbatim ID
      setVerbatimToEdit(selectedVerbatimId); // Set the current verbatim ID for editing
      navigate("/batch-labeling", {
        state: {
          index,
          selectedVerbatims: verbatimsToEdit, // Pass only IDs
        },
      }); // Navigate to the labeling page
    }
  };

  // Toggle verbatim selection for batch editing (store only ID)
  const toggleVerbatimSelection = (verbatimId) => {
    setVerbatimsToEdit((prevSelected) => {
      if (prevSelected.includes(verbatimId)) {
        return prevSelected.filter((id) => id !== verbatimId); // Unselect if already selected
      } else {
        return [...prevSelected, verbatimId]; // Add ID to selected verbatims
      }
    });
  };

  // Select/Deselect all verbatims on the current page
  const handleSelectAll = () => {
    const currentVerbatimIds = paginatedVerbatims.map(
      (verbatim) => verbatim.id
    );
    const allSelected = currentVerbatimIds.every((id) =>
      verbatimsToEdit.includes(id)
    ); // Check if all are selected

    if (allSelected) {
      // If all are already selected, deselect them
      setVerbatimsToEdit((prevSelected) =>
        prevSelected.filter((id) => !currentVerbatimIds.includes(id))
      );
    } else {
      // Otherwise, add the IDs to verbatimsToEdit (making sure no duplicates are added)
      setVerbatimsToEdit((prevSelected) => [
        ...prevSelected,
        ...currentVerbatimIds.filter((id) => !prevSelected.includes(id)), // Add only the unselected IDs
      ]);
    }
  };

  useEffect(() => {
    if (Array.isArray(verbatims) && verbatims.length > 0) {
      // Sort verbatims in descending order by ID
      const sortedVerbatims = [...verbatims].sort((a, b) => (b.id ?? 0) - (a.id ?? 0));

      // Apply the filter function to the sorted verbatims based on the current filters
      let filtered = VerbatimFilter(sortedVerbatims, filters);

      // Apply text search filter
      if (searchQuery.trim() !== "") {
        filtered = filtered.filter(
          (verbatim) =>
            verbatim.verbatim &&
            (() => {
              const verbatimText = typeof verbatim.verbatim === "string"
                ? verbatim.verbatim
                : Array.isArray(verbatim.verbatim)
                  ? verbatim.verbatim
                    .filter((item) => item?.type === "text" && typeof item.content === "string")
                    .map((item) => item.content)
                    .join(" ")
                  : "";

              return verbatimText.toLowerCase().includes(searchQuery.toLowerCase());
            })()
        );
      }

      if (showOnlyDefective) {
        filtered = filtered.filter((verbatim) => getVerbatimDefectInfo(verbatim).isDefective);
      }

      setFilteredVerbatims(filtered); // Update the filtered verbatims state
      setCurrentPage(1); // Reset to the first page whenever verbatims change
    } else {
      setFilteredVerbatims([]); // Ensure filteredVerbatims is an empty array if verbatims is not valid
    }
  }, [verbatims, filters, searchQuery, showOnlyDefective]); // Re-run whenever verbatims, filters, or searchQuery change

  useEffect(() => {
    if(resetBatchBarFields) {
      setTimeout(() => {
        setResetBatchBarFields(false);
      }, 1000); // Delay to ensure the UI updates before resetting
    }
  }, [resetBatchBarFields]);

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters); // Update filters state
  };

  const handleLabelApply = (labelType, labelName, sentimentState, action) => {
    const updatedVerbatims = verbatims.map((v) => {
      if (verbatimsToEdit.includes(v.id)) {
        const newLabel = {
          name: labelName,
          state: sentimentState || "active", // Use selected sentiment state, default to "active" if not provided
          type: labelType,
        };

        const labelGroup = v.labels[labelType] || [];
        const labelExists = labelGroup.some(
          (label) => label.name === labelName
        );

        // Check if multiValue is false; if so, replace the label instead of adding
        if (labelTypes[labelType]?.multiValue === false) {
          v.labels[labelType] = [newLabel]; // Replace with new label
        } else {
          if (action === "add" && !labelExists) {
            v.labels = {
              ...v.labels,
              [labelType]: [...labelGroup, newLabel], // Add new label if multiValue is true
            };
          } else if (action === "add" && labelExists) {
            v.labels = {
              ...v.labels,
              [labelType]: labelGroup.map((l) =>
                l.name === labelName ? newLabel : l
              ),
            };
          } else if (action === "remove" && labelExists) {
            // Remove the label if it exists
            v.labels[labelType] = labelGroup.filter(
              (label) => label.name !== labelName
            );
          }
        }
      }
      return v;
    });

    setResetFilters(true);
    setVerbatims(updatedVerbatims);
    setVerbatimsToEdit([]); // Clear selection after applying labels
    setResetBatchBarFields(true); // Set resetBatchBarFields to true to reset batch bar fields
  };

  /************************************************
   * PAGINATION FUNCTIONS
   ************************************************/

  // Handle the change in the number of verbatims displayed per page
  const handleVerbatimsPerPageChange = (event) => {
    setVerbatimsPerPage(Number(event.target.value)); // Update the state with the new number of verbatims per page
    setCurrentPage(1); // Reset to the first page when changing the number of verbatims per page
  };

  // Handle page changes for pagination
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage); // Update the current page number
  };

  // Ensure all the values are defined and valid
  const safeFilteredVerbatims = Array.isArray(filteredVerbatims)
    ? filteredVerbatims
    : []; // Ensure it's an array
  const safeCurrentPage =
    Number.isInteger(currentPage) && currentPage > 0 ? currentPage : 1; // Default to page 1 if invalid
  const safeVerbatimsPerPage =
    Number.isInteger(verbatimsPerPage) && verbatimsPerPage > 0
      ? verbatimsPerPage
      : 10; // Default to 10 per page if invalid

  // Get the paginated verbatims to display on the current page
  const paginatedVerbatims = safeFilteredVerbatims.slice(
    (safeCurrentPage - 1) * safeVerbatimsPerPage, // Calculate the start index for the current page
    safeCurrentPage * safeVerbatimsPerPage // Calculate the end index for the current page
  );

  // Calculate the total number of pages based on the number of filtered verbatims and the number of verbatims per page
  const totalPages = Math.ceil(
    safeFilteredVerbatims.length / safeVerbatimsPerPage
  );

  /************************************************
   * MISC UI FUNCTIONS
   ************************************************/

  // Toggle the collapsible state (for showing/hiding a collapsible section in the UI)
  const toggleCollapsible = () => {
    setIsCollapsibleOpen(!isCollapsibleOpen); // Toggle the collapsible section's open/close state
  };

  // Handle text-based search input change
  const handleSearchQueryChange = (event) => {
    setSearchQuery(event.target.value); // Update the search query state
  };

  /**
   * Fonction pour afficher le contenu du verbatim (texte ou image)
   * Cette fonction gère intelligemment l'affichage des différents types de contenu dans un verbatim
   */
  const renderVerbatimContent = (verbatim) => {
    if (!verbatim || !verbatim.verbatim) return null; // Ne rien afficher si le contenu est vide

    const content = verbatim.verbatim;

    // Pour les verbatims de l'ancien format (chaîne de texte simple)
    if (typeof content === 'string') {
      return (
        <>
          <span>{content}</span>
        </>
      );
    }

    // Pour les verbatims du nouveau format (tableau de blocs texte/image)
    if (Array.isArray(content)) {
      return (
        <div className="review-verbatim-content">
          {content.map((item, index) => {
            // Afficher le texte s'il est présent
            if (item.type === 'text' && item.content.trim() !== '') {
              return item.content;
            } 
            // Afficher l'image s'il est présente
            else if (item.type === 'image' && item.content) {
              const imageUrl = base64ToImage(item.content);
              return (
                <div key={index} className="review-image-block">
                  <img 
                    src={imageUrl} 
                    alt="Verbatim" 
                    className="review-verbatim-image"
                    onClick={() => setModalImage(imageUrl)}
                    style={{ cursor: 'pointer' }}
                    onError={(e) => {
                      console.error("Erreur de chargement de l'image", e);
                      // Afficher une image par défaut en cas d'erreur
                      e.target.src = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2YwZjBmMCIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMjAiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGZpbGw9IiM5OTkiPkltYWdlIEVycm9yPC90ZXh0Pjwvc3ZnPg==";
                    }}
                  />
                </div>
              );
            }
            return null; // Ne rien afficher pour les blocs vides
          })}
        </div>
      );
    }

    return null; // Ne rien afficher si aucun contenu valide n'est trouvé
  };

  return (
    <div className="batch-edit-page">
      <div className="filter-bar-section">
        <div className="collapsible-container">
          <button className="collapsible-toggle" onClick={toggleCollapsible}>
            {/* Conditionally render the icons based on isCollapsibleOpen */}
            {isCollapsibleOpen ? (
              <i className="fa-solid fa-xmark menu-icon"></i>
            ) : (
              <i className="fa-solid fa-bars menu-icon"></i>
            )}
            {isCollapsibleOpen ? "Hide Menu" : "Show Menu"}
          </button>

          <div
            className="collapsible-content"
            style={{ display: isCollapsibleOpen ? "block" : "none" }}
          >
            <div className="pagination-settings">
              <label htmlFor="verbatims-per-page">Verbatims per page:</label>
              <select
                id="verbatims-per-page"
                value={verbatimsPerPage}
                onChange={handleVerbatimsPerPageChange}
              >
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={500}>500</option>
              </select>
            </div>
            <div className="defective-filter-toggle">
              <label>
                Show only verbatims to correct:
                <input
                  type="checkbox"
                  checked={showOnlyDefective}
                  onChange={(e) => {
                    const nextChecked = e.target.checked;
                    setShowOnlyDefective(nextChecked);

                    if (nextChecked) {
                      setResetFilters(true);
                    }
                  }}
                />
              </label>
            </div>
            <div className="batch-menu-actions">
              <PrimaryButton onClick={() => setIsMergeLabelsModalOpen(true)}><div style={{ width: "158px" }}>Merge labels</div></PrimaryButton>
              <PrimaryButton onClick={() => setIsMergeLabelTypesModalOpen(true)}><div style={{ width: "158px" }}>Merge label types</div></PrimaryButton>
            </div>
          </div>
        </div>
        {/* Search Bar for text-based search */}
        <div className="search-bar">
          <input
            type="text"
            className="search-input"
            id="verbatim-search"
            value={searchQuery}
            onChange={handleSearchQueryChange}
            placeholder="Search for a verbatim"
          />
        </div>
        <div className="filter-bar-container">
          <FilterBar onFilterChange={handleFilterChange} onResetFilters={() => setResetFilters(false)} reset={resetFilters}/>
        </div>
      </div>
      {/* Verbatim Table */}
      <div className="verbatim-table-container">
        <table className="verbatim-table">
          <thead>
            <tr>
              <th className="column-small batch-selection-column">
                <input
                  type="checkbox"
                  checked={
                    verbatimsToEdit.length === paginatedVerbatims.length &&
                    paginatedVerbatims.length > 0
                  }
                  onChange={handleSelectAll} // Call select all function
                />
              </th>
              <th className="column-small defect-id-column">ID</th>
              <th className="column-very-large sticky-column">Verbatim</th>
              <th className="column-very-large">Translation</th>
              {labelTypes &&
                Object.keys(labelTypes).length > 0 &&
                Object.keys(labelTypes).map((labelType) => (
                  <th
                    key={labelType}
                    className={
                      ["Gender", "Age", "Sentiment", "Sources"].includes(labelType)
                        ? "column-medium"
                        : "column-large"
                    }
                  >
                    {labelType}
                  </th>
                ))}
              <th className="column-medium">Links</th>
            </tr>
          </thead>
          <tbody>
            {paginatedVerbatims.length > 0 ? (
              paginatedVerbatims.map((verbatim) => (
                <tr key={verbatim.id}>
                  <td className="column-small batch-selection-column">
                    <input
                      type="checkbox"
                      checked={verbatimsToEdit.includes(verbatim.id)}
                      onChange={() => toggleVerbatimSelection(verbatim.id)}
                    />
                  </td>
                  <td className="column-small defect-id-column">
                    <span className="verbatim-id-cell">
                      <span>{verbatim.id}</span>
                      {(() => {
                        const defectInfo = getVerbatimDefectInfo(verbatim);
                        return defectInfo.isDefective ? (
                          <i
                            className="fa-solid fa-circle-exclamation defective-verbatim-icon"
                            title={defectInfo.message || 'This verbatim needs correction.'}
                          ></i>
                        ) : null;
                      })()}
                    </span>
                  </td>
                  <td className="column-very-large sticky-column">{renderVerbatimContent(verbatim)}</td>
                  <td className="column-very-large">{renderVerbatimTranslation(verbatim.translation)}</td>
                  {labelTypes &&
                    Object.keys(labelTypes).length > 0 &&
                    Object.keys(labelTypes).map((labelType) => (
                      <td
                        key={labelType}
                        className={
                          ["Gender", "Age", "Sentiment", "Sources"].includes(labelType)
                            ? "column-medium"
                            : "column-large"
                        }
                      >
                        {Array.isArray(verbatim.labels?.[labelType])
                          ? verbatim.labels[labelType]
                            .filter((l) => l && l.name) // Filter out any null or undefined labels
                            .map((l) => (
                              <LabelChip
                                key={l.name}
                                type={labelType} // Pass the label type (e.g., "Gender", "Age", "Sentiment")
                                name={l.name} // Pass the name of the label (e.g., "Woman", "25-34", "positive")
                                state={l.state} // Pass the label's state, if it exists
                              />
                            ))
                          : ""}
                      </td>
                    ))}
                  <td className="column-medium">
                    {verbatim.link ? (
                      <a
                        href={verbatim.link}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {extractDomainName(verbatim.link)}
                      </a>
                    ) : (
                      ""
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={labelTypes.length + 5} className="no-verbatims">
                  No verbatims available
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {isFocusEditingOpen && (
          <FocusEditingModal
            show={isFocusEditingOpen}
            verbatims={fullVerbatimsToEdit} // Pass full verbatims instead of IDs
            labelType={selectedLabelType}
            label={selectedLabel}
            sentimentState={selectedSentimentState} // Pass sentiment state to modal
            isSentimentEnabled={labelTypes[selectedLabelType]?.sentimentEnabled} // Check if the label type is sentiment-enabled
            action={selectedAction} // Pass the action to the modal
            onClose={() => {
              setIsFocusEditingOpen(false);
              setVerbatimsToEdit([]);
              setResetFilters(true);
              setResetBatchBarFields(true); // Reset the batch bar state
            }}
            onApplyLabel={handleApplyLabelInModal}
          />
        )}
        {/* Pagination Controls */}
        <div className="pagination-controls">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <span>
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      </div>

      {/* Right panel for selected verbatim actions */}
      <div className="batch-panel">
        <BatchBar
          selectedVerbatimsCount={verbatimsToEdit.length}
          onApplyLabel={(labelType, labelName, sentimentState, action) =>
            handleLabelApply(labelType, labelName, sentimentState, action)
          }
          availableLabels={labels}
          labelTypes={labelTypes}
          selectedLabel={selectedLabel}
          onLabelSelect={setSelectedLabel}
          onFocusEdit={handleFocusEditing}
          resetBatchBarFields={resetBatchBarFields} // Pass the reset state to BatchBar
        />

        <h3>Edit on the labeling page, or delete verbatims</h3>
        <div className="batch-free-actions">
          {/* Add new button for "Batch Free Editing" */}
          <PrimaryButton
            onClick={handleBatchFreeEditing}
            disabled={verbatimsToEdit.length === 0}
          >
            Edit in Labeling Page
          </PrimaryButton>
          <DeleteButton
            onClick={deleteVerbatims}
            disabled={verbatimsToEdit.length === 0}
          >
            Delete all verbatims selected
          </DeleteButton>
        </div>
      </div>
      {modalImage && (<ImageModal  imageUrl={modalImage} onClose={() => setModalImage(null)}/>)}
      <MergeLabelsModal
        isOpen={isMergeLabelsModalOpen}
        onClose={() => {
          setIsMergeLabelsModalOpen(false);
          setResetFilters(true);
        }}
      />
      <MergeLabelTypesModal
        isOpen={isMergeLabelTypesModalOpen}
        onClose={() => {
          setIsMergeLabelTypesModalOpen(false);
          setResetFilters(true);
        }}
      />
    </div>
  );
};

export default BatchEditPage;
